import { useEffect, useMemo, useRef, useState, Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Layout, Button } from "antd";
import { MenuUnfoldOutlined } from "@ant-design/icons";
import { loadMenu, useMenu, useSession } from "../../auth/session";
import LoadingProgress from "../Loader/LoadingProgress";
import useWindowSize from "../../hooks/useWindowSize";
import { findActiveKeys, renderMenuItems } from "./menuUtils";
import Topbar from "./Topbar";
import SidebarNav from "./SidebarNav";
import { neutral } from "../../theme/tokens";

const { Content } = Layout;

/*
 * Dưới mốc này thì mở sidebar là quá tốn chỗ: sidebar 236px + padding trang 48px = 284px,
 * trên laptop 1366 hay MacBook Air đó là gần 1/5 bề ngang màn hình, đủ để hàng chục bảng
 * nhiều cột phải kéo ngang mới đọc hết.
 *
 * Chọn 1500 chứ không phải 1440: MacBook Air 13" có viewport ĐÚNG 1440 và Air M2 là 1470,
 * lấy mốc 1440 thì hai máy phổ biến nhất của đội vận hành rơi ra ngoài. Từ MacBook Pro 14"
 * (1512) trở lên còn đủ chỗ nên giữ sidebar mở.
 */
const NARROW_SCREEN = 1500;

/**
 * Khung ứng dụng (app shell): Topbar + SidebarNav + vùng nội dung (Outlet).
 */
export default function AppLayout() {
  const { user } = useSession();
  const { menuOfUser } = useMenu();

  const { pathname } = useLocation();

  // Thu gọn ngay từ lần render đầu trên màn hẹp, tránh nháy một nhịp rồi mới co lại.
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < NARROW_SCREEN);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);

  const [width] = useWindowSize();
  const isMobile = width < 768;
  const isNarrow = width < NARROW_SCREEN;

  const menuItems = useMemo(
    () => renderMenuItems(menuOfUser?.data, user),
    [menuOfUser, user]
  );

  /*
   * Danh sách nhóm cấp 1 phải lấy từ chính menu vừa dựng.
   *
   * Trước đây chỗ này dùng hằng `rootSubmenuKeys` trong `Slider/adminmenu`, là
   * các chuỗi tay ("customer", "settings"...), trong khi key của menu thật là id
   * số từ API (`renderMenuItems` đặt `key: String(item.id)`). Không bao giờ khớp
   * nên nhánh accordion không lần nào chạy — mọi nhóm cứ mở dồn lên nhau.
   */
  const rootKeys = useMemo(
    () => menuItems.filter((item) => item.children?.length > 0).map((item) => item.key),
    [menuItems]
  );

  const { selectedKeys, parentKeys } = useMemo(
    () => findActiveKeys(menuOfUser?.data, pathname),
    [menuOfUser, pathname]
  );

  // Chuỗi hóa để `useEffect` so được theo giá trị: `parentKeys` là mảng mới sau
  // mỗi lần `useMemo` chạy, để nguyên thì effect bắn lại vô ích.
  const activeParents = parentKeys.join("|");

  // Vào bằng link trực tiếp hoặc F5: mở sẵn nhóm chứa trang hiện tại, không thì
  // item đang chọn nằm trong nhóm đóng và cái highlight thành vô nghĩa.
  useEffect(() => {
    if (activeParents) setOpenKeys(activeParents.split("|"));
  }, [activeParents]);

  // Accordion: mở một nhóm gốc thì đóng nhóm gốc đang mở.
  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));

    // Không có key mới = người dùng vừa đóng một nhóm: giữ nguyên phần còn lại.
    // Key mới không phải nhóm gốc = submenu cấp sâu: phải giữ cả nhóm cha đang mở.
    if (!latestOpenKey || !rootKeys.includes(latestOpenKey)) {
      setOpenKeys(keys);
      return;
    }
    setOpenKeys([latestOpenKey]);
  };

  /*
   * Tự thu gọn khi màn hẹp và tự mở lại khi đủ rộng — CHỈ chạy lúc vượt mốc, không chạy
   * mỗi lần resize, để người dùng bấm mở/thu bằng tay thì giữ nguyên ý họ.
   * Mốc 768 (mobile) nằm trong 1440 nên không cần nhánh riêng: ở mobile sidebar là Drawer.
   */
  const wasNarrowRef = useRef(isNarrow);
  useEffect(() => {
    if (wasNarrowRef.current === isNarrow) return;
    wasNarrowRef.current = isNarrow;
    setCollapsed(isNarrow);
  }, [isNarrow]);

  // Nạp menu. Cache 5 phút do `loadMenu` tự lo, nên gọi thẳng là đủ — không cần
  // tự so `lastFetchTime` ở đây như trước (logic cache từng bị chia đôi giữa
  // component và slice, mỗi bên một nửa).
  useEffect(() => {
    loadMenu().catch(() => {
      // Menu lỗi thì vẫn cho vào app: người dùng còn điều hướng được bằng URL,
      // và interceptor đã lo trường hợp 401.
    });
  }, []);

  return (
    <div className="body">
      <Layout style={{ height: "100vh" }}>
        <Topbar />
        <Layout style={{ background: neutral[50] }}>
          {isMobile && (
            <Button
              type="primary"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{ position: "fixed", left: 20, top: 74, zIndex: 99 }}
            />
          )}

          <SidebarNav
            collapsed={collapsed}
            onToggle={() => setCollapsed((v) => !v)}
            isMobile={isMobile}
            drawerVisible={drawerVisible}
            onDrawerClose={() => setDrawerVisible(false)}
            menuItems={menuItems}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            selectedKeys={selectedKeys}
          />

          {/* overflowX phải là "auto", KHÔNG được "hidden": hidden thì mọi khối rộng quá
              khung bị cắt im lặng, lỗi responsive không ai nhìn thấy để mà sửa. */}
          <Content style={{ overflowY: "auto", overflowX: "auto", minHeight: 0 }}>
            <Suspense fallback={<LoadingProgress />}>
              <Outlet />
            </Suspense>
          </Content>
        </Layout>
      </Layout>
    </div>
  );
}
